import axios from 'axios';

// MCP Server URL - configurable via environment variable
const MCP_SERVER_URL = process.env.REACT_APP_MCP_SERVER_URL || 'https://core.yantrai.com';

// Types
export interface BucketSummary {
  bucket_name: string;
  bucket_category: 'functional' | 'sectoral' | 'historical' | 'important';
  stock_count: number;
}

export interface BucketStock {
  stock_name: string;
  stock_symbol: string | null;
  analysis: string | null;
  financial_info: {
    market_cap?: number;
    pe_ratio?: number;
    revenue_growth?: number;
    profit_margin?: number;
    [key: string]: number | undefined;
  } | null;
  rating: 'strong_buy' | 'buy' | 'hold' | 'sell' | 'strong_sell' | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface BucketDetail {
  bucket_name: string;
  bucket_category: string | null;
  stocks: BucketStock[];
}

export interface StockBucketInfo {
  bucket_name: string;
  bucket_category: string;
  analysis: string | null;
  financial_info: Record<string, number> | null;
  rating: string | null;
}

export interface BucketsResponse {
  buckets: BucketSummary[];
  count: number;
}

export interface StockBucketsResponse {
  stock_name: string;
  buckets: StockBucketInfo[];
  count: number;
}

/**
 * Fetch all buckets with optional category filter
 */
export const fetchBuckets = async (category?: string): Promise<BucketSummary[]> => {
  try {
    const url = category
      ? `${MCP_SERVER_URL}/api/buckets?category=${encodeURIComponent(category)}`
      : `${MCP_SERVER_URL}/api/buckets`;

    console.log('Fetching buckets from:', url);
    const response = await axios.get<BucketsResponse>(url);
    return response.data.buckets;
  } catch (error) {
    console.error('Error fetching buckets:', error);
    throw error;
  }
};

/**
 * Fetch stocks in a specific bucket
 */
export const fetchBucketStocks = async (
  bucketName: string,
  limit: number = 100,
  offset: number = 0
): Promise<BucketDetail> => {
  try {
    const url = `${MCP_SERVER_URL}/api/buckets/${encodeURIComponent(bucketName)}/stocks?limit=${limit}&offset=${offset}`;
    console.log('Fetching bucket stocks from:', url);
    const response = await axios.get<BucketDetail>(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching bucket stocks:', error);
    throw error;
  }
};

/**
 * Fetch all buckets that contain a specific stock
 */
export const fetchStockBuckets = async (stockName: string): Promise<StockBucketInfo[]> => {
  try {
    const url = `${MCP_SERVER_URL}/api/stocks/${encodeURIComponent(stockName)}/buckets`;
    console.log('Fetching stock buckets from:', url);
    const response = await axios.get<StockBucketsResponse>(url);
    return response.data.buckets;
  } catch (error) {
    console.error('Error fetching stock buckets:', error);
    throw error;
  }
};

/**
 * Initialize the bucket_stocks table (admin only)
 */
export const initBucketTable = async (): Promise<boolean> => {
  try {
    const url = `${MCP_SERVER_URL}/api/buckets/init`;
    console.log('Initializing bucket table:', url);
    const response = await axios.post(url);
    return response.status === 200;
  } catch (error) {
    console.error('Error initializing bucket table:', error);
    return false;
  }
};
